/* eslint-disable max-lines-per-function */
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useClick,
  useRole,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { ReactNode } from 'react';

import { XmarkIcon, mergeStyles, portalElement } from '../..';

export interface ElementClassNames {
  base: string;
  centered?: string;
  nonCentered?: string;
}

export type ElementClassNamesKey =
  | 'body'
  | 'close'
  | 'closeIcon'
  | 'container'
  | 'footer'
  | 'modal'
  | 'overlay'
  | 'title';
export type ModalClassNames = Partial<Record<ElementClassNamesKey, ElementClassNames>>;
export type UserClassNames = Partial<Record<ElementClassNamesKey, string>>;

export interface ModalProps {
  /** Modal title, stays fixed on top of the modal when centered and body content overflows. */
  title?: ReactNode;
  /** Modal footer, stays fixed on top of the modal when centered and body content overflows. */
  footer?: ReactNode;
  children?: ReactNode;
  /** Whether the modal is vertically centered? By default it is. */
  centered?: boolean;
  /** Whether to display a close icon for closing the associated modal. */
  closable?: boolean;
  /** Whether to close the modal when clicking outside of it. */
  closeOnClickOutside?: boolean;
  /**
   * Pass-thru prop to FloatingFocusManager. By default the focus manager focuses on the first focusable element in the
   * modal. Setting this to a negative number means the focus manager will not focus on any element.
   * See https://floating-ui.com/docs/floatingfocusmanager#initialfocus for more info on its usage.
   */
  initialFocus?: number | React.MutableRefObject<HTMLElement | null>;
  /** Whether the modal is visible when initialized. */
  visible?: boolean;
  /**
   * Custom class names for the modal elements. It supports the following keys:
   * - `overlay`
   * - `container`
   * - `modal`
   * - `close`
   * - `closeIcon`
   * - `title`
   * - `body`
   * - `footer`
   */
  classNames?: UserClassNames;
  /** Callback function for when the modal is closed. */
  onCancel?: () => void;
}

const classNames: ModalClassNames = {
  overlay: {
    base: 'bg-black/45 z-50',
    centered: 'grid place-items-center',
    nonCentered: 'flex justify-center items-start',
  },
  container: {
    base: 'divide-y divide-gray-300',
  },
  modal: {
    base: 'modal relative rounded-lg shadow-dark bg-white border-white',
    nonCentered: 'top-25',
  },
  close: {
    base: 'modal-close absolute right-1 h-13.5 grid place-items-center cursor-pointer',
  },
  closeIcon: {
    base: 'modal-close-icon w-16 h-8 leading-13',
  },
  title: {
    base: 'modal-title py-4 px-6 font-medium text-lg leading-5.5',
  },
  body: {
    base: 'modal-body p-6 text-base max-w-[calc(80vw)]',
    centered: 'max-h-[calc(70vh)] overflow-y-auto modal-body-centered',
  },
  footer: {
    base: 'modal-footer py-2.5 px-4 text-base text-right gap-x-2',
  },
};

export const Modal = (props: ModalProps) => {
  const {
    children,
    title,
    footer,
    centered = true,
    initialFocus = 0,
    visible = false,
    closable = true,
    closeOnClickOutside = true,
    onCancel,
    classNames: userClassNames,
  } = props;
  const { refs, context } = useFloating({
    open: visible,
    onOpenChange: () => {
      onCancel?.();
    },
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { enabled: closeOnClickOutside, outsidePressEvent: 'mousedown' });
  const { getFloatingProps } = useInteractions([click, role, dismiss]);

  const getClassNames = (element: ElementClassNamesKey) => {
    const elementClassNames = classNames[element];
    const baseClassNames = elementClassNames?.base;
    const centeredClass = centered ? elementClassNames?.centered : elementClassNames?.nonCentered;
    const userClassName = userClassNames?.[element] ?? '';
    // mergeStyles will override predefined tailwind CSS classes, remove duplicates and sort them
    return mergeStyles([baseClassNames, centeredClass, userClassName]);
  };

  return (
    <>
      {visible === true ? (
        <FloatingPortal root={portalElement() as HTMLElement}>
          <FloatingOverlay className={getClassNames('overlay')} lockScroll>
            <FloatingFocusManager context={context} initialFocus={initialFocus}>
              <div ref={refs.setFloating} {...getFloatingProps()} className={getClassNames('modal')}>
                {closable && (
                  <div className={getClassNames('close')}>
                    <XmarkIcon className={getClassNames('closeIcon')} onClick={onCancel} />
                  </div>
                )}
                <div className={getClassNames('container')}>
                  {title && <div className={getClassNames('title')}>{title}</div>}
                  <div className={getClassNames('body')}>{children}</div>
                  {footer && <div className={getClassNames('footer')}>{footer}</div>}
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      ) : undefined}
    </>
  );
};

export default Modal;
